import { createPrecipSampler } from "@divespots/contracts";
import type { PrecipField } from "@divespots/contracts";
import type { Map as LeafletMap } from "leaflet";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { L } from "../leaflet-global.ts";
import { precipColors, rasterSize, rasterize } from "./raster.ts";
import type { Rgba } from "./raster.ts";
import { hourStart } from "./time.ts";

/** Above the tiles (200) and below the wind pane (350) and the overlay/marker panes (400+). */
const PANE = "precip";
const PANE_Z = 340;
const KM_PER_DEG = 111.32;

const readToken = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/**
 * Canvas layer of the precipitation overlay: one static image per hour. The viewport is sampled
 * onto a coarse raster (about 1 px per km), coloured by class, and the browser scales it up with
 * smoothing. There is no animation loop; it redraws on hour change, move/zoom end, colour scheme
 * and layer change.
 */
export class PrecipCanvasLayer extends L.Layer {
  private canvas: HTMLCanvasElement | null = null;
  private mapRef: LeafletMap | null = null;
  private sampler: ReturnType<typeof createPrecipSampler> | null = null;
  private tMs = 0;
  private colors: Rgba[] = precipColors(readToken);
  private drawnPx = 0;
  private classCounts = [0, 0, 0, 0];
  private scheme: MediaQueryList | null = null;

  /** Coloured pixels of the last draw (E2E hook). */
  get drawn(): number {
    return this.drawnPx;
  }
  /** Coloured pixels per class (light..very heavy) of the last draw (E2E hook). */
  get byClass(): number[] {
    return this.classCounts;
  }
  /** Start of the hour being shown. */
  get hourStartMs(): number {
    return hourStart(this.tMs);
  }

  override onAdd(map: LeafletMap): this {
    this.mapRef = map;
    const pane = map.getPane(PANE) ?? map.createPane(PANE);
    pane.style.zIndex = String(PANE_Z);
    pane.style.pointerEvents = "none";
    const canvas = document.createElement("canvas");
    canvas.className = "precip-canvas leaflet-zoom-hide";
    canvas.setAttribute("aria-hidden", "true");
    pane.appendChild(canvas);
    this.canvas = canvas;
    map.on("moveend zoomend resize", this.draw, this);
    this.scheme = window.matchMedia?.("(prefers-color-scheme: dark)") ?? null;
    this.scheme?.addEventListener?.("change", this.recolor);
    map.whenReady(this.draw, this);
    return this;
  }

  override onRemove(map: LeafletMap): this {
    map.off("moveend zoomend resize", this.draw, this);
    this.scheme?.removeEventListener?.("change", this.recolor);
    this.scheme = null;
    this.canvas?.remove();
    this.canvas = null;
    this.mapRef = null;
    this.drawnPx = 0;
    return this;
  }

  setField(field: Pick<PrecipField, "grid" | "times" | "p">): void {
    this.sampler = createPrecipSampler(field);
    this.draw();
  }

  setTime(tMs: number): void {
    const hourChanged = hourStart(tMs) !== hourStart(this.tMs);
    this.tMs = tMs;
    if (hourChanged) this.draw();
  }

  /** Layer or theme changed: read the tokens again and redraw. */
  recolor = (): void => {
    this.colors = precipColors(readToken);
    this.draw();
  };

  private draw = (): void => {
    const map = this.mapRef;
    const canvas = this.canvas;
    const sampler = this.sampler;
    if (!map || !canvas || !sampler) return;
    const s = map.getSize();
    if (s.x === 0 || s.y === 0) return;
    const b = map.getBounds();
    const midLat = (b.getNorth() + b.getSouth()) / 2;
    const { w, h } = rasterSize(
      (b.getEast() - b.getWest()) * KM_PER_DEG * Math.cos((midLat * Math.PI) / 180),
      (b.getNorth() - b.getSouth()) * KM_PER_DEG,
    );
    const t = this.tMs;
    const { data, drawn, byClass } = rasterize(
      (x, y) => {
        const ll = map.containerPointToLatLng([(x / w) * s.x, (y / h) * s.y]);
        return sampler(ll.lng, ll.lat, t);
      },
      w,
      h,
      this.colors,
    );
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = `${s.x}px`;
    canvas.style.height = `${s.y}px`;
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    canvas.getContext("2d")?.putImageData(new ImageData(data, w, h), 0, 0);
    this.drawnPx = drawn;
    this.classCounts = byClass;
  };
}

interface Props {
  field: PrecipField;
  /** Forecast time in epoch ms; the layer shows the hour that contains it. */
  t: number;
  aerial: boolean;
}

/** react-leaflet child of the overview map: mounted only while the overlay is on. */
export function PrecipLayer({ field, t, aerial }: Props) {
  const map = useMap();
  const layer = useRef<PrecipCanvasLayer | null>(null);

  useEffect(() => {
    const l = new PrecipCanvasLayer();
    layer.current = l;
    l.addTo(map);
    // E2E builds only: the tests read what the overlay drew from here
    if (import.meta.env.VITE_E2E === "1") {
      Object.defineProperty(window, "__precip", {
        configurable: true,
        get: () => ({ drawn: l.drawn, hourStartMs: l.hourStartMs, byClass: l.byClass }),
      });
    }
    return () => {
      l.remove();
      layer.current = null;
      if (import.meta.env.VITE_E2E === "1") delete (window as { __precip?: unknown }).__precip;
    };
  }, [map]);

  // time before the field so the first draw already shows the right hour
  useEffect(() => layer.current?.setTime(t), [t]);
  useEffect(() => layer.current?.setField(field), [field]);
  useEffect(() => layer.current?.recolor(), [aerial]);
  return null;
}
